import Link from 'found/Link';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { defineMessages, useIntl } from 'react-intl';
import { graphql, usePaginationFragment } from 'react-relay';

import { AdminTablePlaceholderRows } from 'src/components/Admin/AdminTablePlaceholder';
import BadgeActive from 'src/components/Badge/BadgeActive';
import BadgeActiveTimeRange from 'src/components/Badge/BadgeActiveTimeRange';
import PlaceholderErrorModal from 'src/components/ErrorBoundary/PlaceholderErrorBoundary/PlaceholderErrorModal';
import LoadMoreButton from 'src/components/LoadMoreButton';
import UUID from 'src/components/UUID';
import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import { DEFAULT_LIST_PAGE_SIZE } from 'src/util/constants';
import logger from 'src/util/logger';

const messages = defineMessages({
  paginationError: {
    id: 'admin.admin_tariff.admin_tariff_list.table.pagination_error.message',
    defaultMessage: 'Failed to load more tariffs. Please try again.',
  },
});

const AdminTariffListFragment = graphql`
  fragment AdminTariffList_viewer on Viewer
  @throwOnFieldError
  @refetchable(queryName: "AdminTariffListRefetchQuery")
  @argumentDefinitions(
    cursor: { type: "String" }
    count: { type: "Int" }
  )
  {
    id
    tariffs(
      first: $count
      after: $cursor
    ) @connection(key: "AdminTariffList_tariffs") {
      edges {
        cursor
        node {
          uuid
          externalIdentifier
          identifier
          title
          tariffType
          active {
            start
            finish
          }
          meter {
            uuid
            identifier
            active { start finish }
          }
          property {
            uuid
            title
            active { start finish }
          }
        }
      }
    }
  }
`;

/**
 * Skeleton rows rendered behind the pagination error modal, inside the loaded
 * table, to stand in for the page that failed to load.
 * @returns {React.ReactElement} Skeleton table rows.
 */
function PaginationErrorRows() {
  return <AdminTablePlaceholderRows columnCount={8} rowCount={3} />;
}

/**
 * Admin list of tariffs with load-more pagination. Field errors throw to the
 * route's PlaceholderErrorBoundary; load-more failures show an inline retry modal.
 * @param {object} props - Component props.
 * @param {object|null} props.viewer - Relay fragment reference for the viewer.
 * @returns {React.ReactElement} AdminTariffList component.
 */
function AdminTariffList({ viewer: viewerRef = null }) {
  const {
    data: viewer, loadNext, hasNext, isLoadingNext,
  } = usePaginationFragment(AdminTariffListFragment, viewerRef);
  const [paginationError, setPaginationError] = useState(null);

  const loadMore = () => {
    if (!hasNext || isLoadingNext) {
      return;
    }

    setPaginationError(null);
    loadNext(DEFAULT_LIST_PAGE_SIZE, {
      onComplete: (error) => {
        if (error) {
          logger.error({ error });
          setPaginationError(error);
        }
      },
    });
  };

  const intl = useIntl();

  if (!viewer) {
    // A completed query without a viewer would otherwise never leave the spinner.
    throw new Error('AdminTariffList: the route query returned no viewer');
  }

  const { tariffs } = viewer;

  // Page title and breadcrumbs
  const pageTitle = intl.formatMessage({
    id: 'admin.admin_tariff.admin_tariff_list.page.title',
    defaultMessage: 'Enosi Admin - Tariffs',
  });
  const breadcrumbsAdmin = intl.formatMessage({
    id: 'admin.admin_tariff.admin_tariff_list.breadcrumbs.admin.label',
    defaultMessage: 'Admin',
  });
  const breadcrumbsTariffs = intl.formatMessage({
    id: 'admin.admin_tariff.admin_tariff_list.breadcrumbs.tariffs.label',
    defaultMessage: 'Tariffs',
  });

  // Table headers
  const statusHeader = intl.formatMessage({
    id: 'admin.admin_tariff.admin_tariff_list.table.head.status.label',
    defaultMessage: 'Status',
  });
  const titleHeader = intl.formatMessage({
    id: 'admin.admin_tariff.admin_tariff_list.table.head.title.label',
    defaultMessage: 'Title',
  });
  const identifierHeader = intl.formatMessage({
    id: 'admin.admin_tariff.admin_tariff_list.table.head.identifier.label',
    defaultMessage: 'Identifier',
  });
  const idHeader = intl.formatMessage({
    id: 'admin.admin_tariff.admin_tariff_list.table.head.id.label',
    defaultMessage: 'ID',
  });
  const externalIdHeader = intl.formatMessage({
    id: 'admin.admin_tariff.admin_tariff_list.table.head.external_identifier.title',
    defaultMessage: 'External Identifier',
  });
  const externalIdAbbrLabel = intl.formatMessage({
    id: 'admin.admin_tariff.admin_tariff_list.table.head.external_identifier.abbr.label',
    defaultMessage: 'Ext ID',
  });
  const tariffTypeHeader = intl.formatMessage({
    id: 'admin.admin_tariff.admin_tariff_list.table.head.tariff_type.label',
    defaultMessage: 'Tariff Type',
  });
  const meterHeader = intl.formatMessage({
    id: 'admin.admin_tariff.admin_tariff_list.table.head.meter.label',
    defaultMessage: 'Meter',
  });
  const propertyHeader = intl.formatMessage({
    id: 'admin.admin_tariff.admin_tariff_list.table.head.property.label',
    defaultMessage: 'Property',
  });

  // Load more button text
  const copyLoadMore = intl.formatMessage({
    id: 'admin.admin_tariff.admin_tariff_list.load_more_button.state_load_more.label',
    defaultMessage: 'Load more...',
  });
  const copyNoMore = intl.formatMessage({
    id: 'admin.admin_tariff.admin_tariff_list.load_more_button.state_no_more.label',
    defaultMessage: 'No more tariffs to load',
  });

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>

      <Breadcrumbs
        breadcrumbs={[
          { name: breadcrumbsAdmin, path: '/admin' },
          { name: breadcrumbsTariffs },
        ]}
      />

      <div className="clearfix">
        <h1 className="float-start">{breadcrumbsTariffs}</h1>
      </div>

      <div className="mt-4 mb-4 card">
        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{statusHeader}</th>
                <th scope="col">{titleHeader}</th>
                <th scope="col">{identifierHeader}</th>
                <th scope="col">{idHeader}</th>
                <th scope="col">
                  <abbr title={externalIdHeader}>{externalIdAbbrLabel}</abbr>
                </th>
                <th scope="col">{tariffTypeHeader}</th>
                <th scope="col">{meterHeader}</th>
                <th scope="col">{propertyHeader}</th>
              </tr>
            </thead>
            <tbody>
              {
                tariffs && tariffs.edges && tariffs.edges.map((edge) => {
                  const { node } = edge;
                  const {
                    uuid, title, identifier, externalIdentifier, active,
                    tariffType, meter, property,
                  } = node;
                  const {
                    uuid: meterId, identifier: meterIdentifier, active: meterActive,
                  } = meter;
                  const {
                    uuid: propertyId, title: propertyTitle, active: propertyActive,
                  } = property;

                  return (
                    <tr key={`admin-tariff-${uuid}`}>
                      <td>
                        <BadgeActiveTimeRange active={active} />
                      </td>
                      <th scope="row" colSpan={2}>
                        <Link to={`/admin/tariffs/${uuid}`}>
                          {title}
                        </Link>
                        <br />
                        {identifier}
                      </th>
                      <td colSpan={2}>
                        <UUID uuid={uuid} />
                        <br />
                        {externalIdentifier}
                      </td>
                      <td>
                        {tariffType}
                      </td>
                      <td>
                        <Link to={`/admin/meters/${meterId}`}>
                          {meterIdentifier}
                        </Link>
                        <br />
                        <BadgeActive start={meterActive.start} finish={meterActive.finish} />
                      </td>
                      <td>
                        <Link to={`/admin/properties/${propertyId}`}>
                          {propertyTitle}
                        </Link>
                        <br />
                        <BadgeActive start={propertyActive.start} finish={propertyActive.finish} />
                      </td>
                    </tr>
                  );
                })
              }
              {/* The modal renders through a portal; only its skeleton rows render here. */}
              {paginationError && (
                <PlaceholderErrorModal
                  bodyText={intl.formatMessage(messages.paginationError)}
                  error={paginationError}
                  onCancelFunc={() => setPaginationError(null)}
                  onRetryFunc={loadMore}
                  placeholderComponent={PaginationErrorRows}
                  retryDisabled={isLoadingNext}
                />
              )}
            </tbody>
          </table>
        </div>
        <div className="card-footer">
          <LoadMoreButton
            copyLoadMore={copyLoadMore}
            copyNoMore={copyNoMore}
            onClick={loadMore}
            hasNext={hasNext}
          />
        </div>
      </div>
    </>
  );
}

AdminTariffList.propTypes = {
  viewer: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

export default AdminTariffList;
